import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  viewChildren,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { TopNavigationItem } from '@core/navigation/models/top-navigation/top-navigation-item.interface';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { FeaturesDirective } from '@core/features/directives/features.directive';
import { SpinnerComponent, PrefetchLinkDirective } from '@kaseya-design-components';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { TopNavigationPath } from '@core/navigation/models/top-navigation/top-navigation-path';
import { SsoTargetPage } from '@design/open-sso-link/models/sso-target-page.enum';
import { SsoSalesforceDestination } from '@design/open-sso-link/models/sso-salesforce-destination.enum';
import { BehaviorSubject, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OpenSsoLinkDirective } from '@design/open-sso-link/directives/open-sso-link.directive';
import { OpenSsoSalesforceLinkDirective } from '@design/open-sso-link/directives/open-sso-salesforce-link.directive';
import { PermissionDirective } from '@core/permissions/directives/permission.directive';
import { Permission } from '@kaseya-shared';
import { CountryAccessDirective } from '@core/locations/directives/country-access.directive';
import { AuthorizationService } from '@core/authorization/services/authorization.service';
import { focusAdjacentElement, MenuNavigationDirection } from '@kaseya-utils';

@Component({
  selector: 'itc-top-navigation-dropdown',
  templateUrl: 'top-navigation-dropdown.component.html',
  styleUrl: 'top-navigation-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'closeAndFocusTrigger()',
    '(document:focusin)': 'closeOnOutsideFocus($event)',
    '(keydown.arrowDown)': 'moveFocus($event, MenuNavigationDirection.Next)',
    '(keydown.arrowUp)': 'moveFocus($event, MenuNavigationDirection.Previous)',
  },
  imports: [
    TranslatePipe,
    AsyncPipe,
    NgTemplateOutlet,
    FeaturesDirective,
    SpinnerComponent,
    RouterLinkActive,
    RouterLink,
    PrefetchLinkDirective,
    OpenSsoLinkDirective,
    OpenSsoSalesforceLinkDirective,
    PermissionDirective,
    CountryAccessDirective,
  ],
})
export class TopNavigationDropdownComponent {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dropdownItems = viewChildren<ElementRef<HTMLElement>>('dropdownItem');

  readonly trigger = input.required<CdkOverlayOrigin>();
  readonly navigationItems = input.required<TopNavigationItem[]>();
  readonly isDropdownOpen = model<boolean>();
  readonly TopNavigationPath = TopNavigationPath;
  readonly SsoTargetPage = SsoTargetPage;
  readonly SsoSalesforceDestination = SsoSalesforceDestination;
  readonly Permission = Permission;
  readonly MenuNavigationDirection = MenuNavigationDirection;
  readonly isKaseyaStoreSsoLinkLoading$ = new BehaviorSubject(false);

  constructor() {
    this.focusFirstItemAfterOpen();
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: MouseEvent): void {
    if (
      !event.composedPath().includes(this.elementRef.nativeElement) &&
      !event.composedPath().includes(this.trigger().elementRef.nativeElement)
    ) {
      this.isDropdownOpen.set(false);
    }
  }

  get isMigratedUser(): boolean {
    return this.authorizationService.isMigratedUser();
  }

  closeAndFocusTrigger(): void {
    this.isDropdownOpen.set(false);
    this.trigger().elementRef.nativeElement.focus();
  }

  closeOnOutsideFocus({ target }: FocusEvent): void {
    const focusedElement = target as Node;
    const isFocusInsideDropdownOrTrigger =
      this.elementRef.nativeElement.contains(focusedElement) || this.trigger().elementRef.nativeElement.contains(focusedElement);

    if (!isFocusInsideDropdownOrTrigger) {
      this.isDropdownOpen.set(false);
    }
  }

  moveFocus(event: Event, direction: MenuNavigationDirection): void {
    event.preventDefault();
    focusAdjacentElement(
      this.dropdownItems().map(({ nativeElement }) => nativeElement),
      direction
    );
  }

  closeDropdownWhenLoadingIsDone(): void {
    this.isKaseyaStoreSsoLinkLoading$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(isLoading => !isLoading)
      )
      .subscribe(() => this.isDropdownOpen.set(false));
  }

  private focusFirstItemAfterOpen(): void {
    afterNextRender(() => this.dropdownItems()[0]?.nativeElement.focus());
  }
}
